import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Download, Image as ImageIcon, Droplets, Copy, Palette, Eye } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface ColorInfo {
  hex: string;
  rgb: { r: number; g: number; b: number };
  hsl: { h: number; s: number; l: number };
  x: number;
  y: number;
}

interface ColorPalette {
  colors: ColorInfo[];
  name: string;
}

export const ImageColorPickerTool: React.FC = () => {
  const { t } = useTranslation();
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<ColorInfo | null>(null);
  const [colorHistory, setColorHistory] = useState<ColorInfo[]>([]);
  const [dominantColors, setDominantColors] = useState<ColorInfo[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showCrosshair, setShowCrosshair] = useState(true);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error(t('tools.imageColorPicker.invalidImageError'));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target?.result as string);
        setSelectedColor(null);
        setColorHistory([]);
        setDominantColors([]);
      };
      reader.readAsDataURL(file);
    }
  };

  const rgbToHex = (r: number, g: number, b: number): string => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  const rgbToHsl = (r: number, g: number, b: number): { h: number; s: number; l: number } => {
    r /= 255;
    g /= 255;
    b /= 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  const getPixelColor = useCallback((x: number, y: number): ColorInfo | null => {
    if (!canvasRef.current || !imageRef.current) return null;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const rect = imageRef.current.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const canvasX = Math.floor(x * scaleX);
    const canvasY = Math.floor(y * scaleY);

    const imageData = ctx.getImageData(canvasX, canvasY, 1, 1);
    const [r, g, b] = imageData.data;

    const hex = rgbToHex(r, g, b);
    const hsl = rgbToHsl(r, g, b);

    return {
      hex,
      rgb: { r, g, b },
      hsl,
      x: canvasX,
      y: canvasY
    };
  }, []);

  const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const color = getPixelColor(x, y);
    if (color) {
      setSelectedColor(color);
      setColorHistory(prev => {
        const newHistory = [color, ...prev.filter(c => c.hex !== color.hex)];
        return newHistory.slice(0, 20); // Keep last 20 colors
      });
    }
  };

  const handleImageMouseMove = (event: React.MouseEvent<HTMLImageElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    setMousePosition({ x, y });
  };

  const analyzeDominantColors = useCallback(async () => {
    if (!originalImage || !canvasRef.current) return;

    setIsAnalyzing(true);

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        // Sample colors from the image
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        const colorMap = new Map<string, number>();

        // Sample every 10th pixel to improve performance
        for (let i = 0; i < data.length; i += 40) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const hex = rgbToHex(r, g, b);
          colorMap.set(hex, (colorMap.get(hex) || 0) + 1);
        }

        // Get top 8 most frequent colors
        const sortedColors = Array.from(colorMap.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 8)
          .map(([hex]) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            const hsl = rgbToHsl(r, g, b);
            return {
              hex,
              rgb: { r, g, b },
              hsl,
              x: 0,
              y: 0
            };
          });

        setDominantColors(sortedColors);
        setIsAnalyzing(false);
      };

      img.src = originalImage;
    } catch (error) {
      console.error('Color analysis error:', error);
      toast.error(t('tools.imageColorPicker.analysisError'));
      setIsAnalyzing(false);
    }
  }, [originalImage]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const exportPalette = () => {
    const palette: ColorPalette = {
      name: 'Extracted Palette',
      colors: [...(selectedColor ? [selectedColor] : []), ...colorHistory, ...dominantColors]
    };

    const blob = new Blob([JSON.stringify(palette, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'color-palette.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Load image to canvas when image changes
  React.useEffect(() => {
    if (originalImage && canvasRef.current && imageRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
      };
      img.src = originalImage;
    }
  }, [originalImage]);

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Droplets className="w-5 h-5" />
            <span>{t('common.imageColorPicker')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('common.dragDropImage')}
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
              >
                <Upload className="w-4 h-4 mr-2" />
                {t('common.selectImage')}
              </Button>
            </div>

            {originalImage && (
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={analyzeDominantColors}
                  disabled={isAnalyzing}
                  variant="primary"
                >
                  <Palette className="w-4 h-4 mr-2" />
                  {isAnalyzing ? 'Analyzing...' : 'Analyze Colors'}
                </Button>
                <Button
                  onClick={() => setShowCrosshair(!showCrosshair)}
                  variant="outline"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {showCrosshair ? 'Hide' : 'Show'} Crosshair
                </Button>
                <Button
                  onClick={exportPalette}
                  variant="outline"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Palette
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Image and Color Picker */}
      {originalImage && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Click on the image to pick colors</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative inline-block">
                  <img
                    ref={imageRef}
                    src={originalImage}
                    alt="Color picker source"
                    className="max-w-full h-auto cursor-crosshair border rounded"
                    onClick={handleImageClick}
                    onMouseMove={handleImageMouseMove}
                    style={{ maxHeight: '500px' }}
                  />
                  {showCrosshair && (
                    <div
                      className="absolute pointer-events-none"
                      style={{
                        left: mousePosition.x - 10,
                        top: mousePosition.y - 10,
                        width: 20,
                        height: 20,
                        border: '2px solid white',
                        borderRadius: '50%',
                        boxShadow: '0 0 0 1px black',
                      }}
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            {/* Selected Color */}
            {selectedColor && (
              <Card>
                <CardHeader>
                  <CardTitle>Selected Color</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div
                      className="w-full h-20 rounded border-2 border-gray-300"
                      style={{ backgroundColor: selectedColor.hex }}
                    />
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span>HEX:</span>
                        <div className="flex items-center space-x-2">
                          <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                            {selectedColor.hex}
                          </code>
                          <Button
                            onClick={() => copyToClipboard(selectedColor.hex)}
                            size="sm"
                            variant="outline"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>RGB:</span>
                        <div className="flex items-center space-x-2">
                          <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                            {selectedColor.rgb.r}, {selectedColor.rgb.g}, {selectedColor.rgb.b}
                          </code>
                          <Button
                            onClick={() => copyToClipboard(`rgb(${selectedColor.rgb.r}, ${selectedColor.rgb.g}, ${selectedColor.rgb.b})`)}
                            size="sm"
                            variant="outline"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>HSL:</span>
                        <div className="flex items-center space-x-2">
                          <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                            {selectedColor.hsl.h}°, {selectedColor.hsl.s}%, {selectedColor.hsl.l}%
                          </code>
                          <Button
                            onClick={() => copyToClipboard(`hsl(${selectedColor.hsl.h}, ${selectedColor.hsl.s}%, ${selectedColor.hsl.l}%)`)}
                            size="sm"
                            variant="outline"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Dominant Colors */}
            {dominantColors.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Dominant Colors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-2">
                    {dominantColors.map((color, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedColor(color)}
                        className="w-full h-12 rounded border-2 border-gray-300 hover:scale-105 transition-transform"
                        style={{ backgroundColor: color.hex }}
                        title={color.hex}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Color History */}
            {colorHistory.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Recent Colors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-2">
                    {colorHistory.map((color, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedColor(color)}
                        className="w-full h-8 rounded border border-gray-300 hover:scale-105 transition-transform"
                        style={{ backgroundColor: color.hex }}
                        title={color.hex}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};
